// ===========================================
// Lesson Notes Card Component
// ===========================================

import { Paper, Typography, Box, Button, Divider, CircularProgress } from '@mui/material';
import { Notes as NotesIcon } from '@mui/icons-material';
import { Note } from '../../../api/notes.api';
import LessonNoteItem from './LessonNoteItem';

interface LessonNotesCardProps {
  notes: Note[] | undefined;
  isLoading: boolean;
  onViewAllClick: () => void;
}

export default function LessonNotesCard({ notes, isLoading, onViewAllClick }: LessonNotesCardProps) {
  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <NotesIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          Class Notes
        </Typography>
        {/* Ungated on purpose (2026-09-15). This was `notes.length > 5`, so
            with five or fewer notes there was no expand control at all and a
            clamped note could not be read from this page. */}
        {notes && notes.length > 0 && (
          <Button size="small" onClick={onViewAllClick}>
            View All ({notes.length})
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : notes && notes.length > 0 ? (
        <>
          {notes.slice(0, 5).map((note) => (
            <LessonNoteItem key={note.id} note={note} />
          ))}
          {/* Deliberately still gated at > 5, unlike the header button above
              (2026-09-15). This one is a convenience duplicate for a list long
              enough that the header button has scrolled out of view — the card
              renders at most 5 notes, so below that threshold the header button
              is still on screen and a second identical control would be noise.
              Both call the same handler. */}
          {notes.length > 5 && (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={onViewAllClick}
              sx={{ mt: 1 }}
            >
              View All Notes ({notes.length})
            </Button>
          )}
        </>
      ) : (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <NotesIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            No notes yet for this class
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
